/* eslint arrow-body-style: 0 */
import EmailFolderView from '../emailFolders/EmailFolderView.vue';
import { frontendURL } from '../../../helper/URLHelper';
import store from '../../../store';
import ConversationKanban from './ConversationKanban.vue';
import ConversationView from './ConversationView.vue';
import ConversationFilters from './ConversationFilters.vue';
import OperationalHome from '../operationalHome/Index.vue';

const CONVERSATION_PERMISSIONS = [
  'administrator',
  'agent',
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
];

const isFolderAvailable = async folderId => {
  let folders = store.getters['customViews/getConversationCustomViews'];
  if (!folders.length) {
    await store.dispatch('customViews/get', 'conversation');
    folders = store.getters['customViews/getConversationCustomViews'];
  }
  return folders.some(folder => folder.id === Number(folderId));
};

const redirectFolderListIfUnavailable = async (to, _from, next) => {
  if (await isFolderAvailable(to.params.id)) {
    next();
    return;
  }
  next({
    name: 'talkvia_conversations',
    params: { accountId: to.params.accountId },
  });
};

const redirectFolderConversationIfUnavailable = async (to, _from, next) => {
  if (await isFolderAvailable(to.params.id)) {
    next();
    return;
  }
  next({
    name: 'inbox_conversation',
    params: {
      accountId: to.params.accountId,
      conversation_id: to.params.conversation_id,
    },
  });
};

const conversationRoutes = [
  {
    path: frontendURL('accounts/:accountId/dashboard'),
    name: 'home',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: OperationalHome,
  },
  {
    path: frontendURL('accounts/:accountId/atendimentos'),
    name: 'talkvia_conversations',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: () => {
      return { inboxId: 0 };
    },
  },
  {
    path: frontendURL('accounts/:accountId/conversation-filters/:filterType'),
    name: 'conversation_filters',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationFilters,
    props: route => ({
      filterType: ['inboxes', 'labels'].includes(route.params.filterType)
        ? route.params.filterType
        : 'inboxes',
    }),
  },
  {
    path: frontendURL('accounts/:accountId/kanban'),
    name: 'conversation_kanban',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationKanban,
  },
  {
    path: frontendURL('accounts/:accountId/conversations/:conversation_id'),
    name: 'inbox_conversation',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => {
      return { inboxId: 0, conversationId: route.params.conversation_id };
    },
  },
  {
    path: frontendURL('accounts/:accountId/inbox/:inbox_id'),
    name: 'inbox_dashboard',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => {
      return { inboxId: route.params.inbox_id };
    },
  },
  {
    path: frontendURL(
      'accounts/:accountId/inbox/:inbox_id/conversations/:conversation_id'
    ),
    name: 'conversation_through_inbox',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => {
      return {
        conversationId: route.params.conversation_id,
        inboxId: route.params.inbox_id,
      };
    },
  },
  {
    path: frontendURL('accounts/:accountId/label/:label'),
    name: 'label_conversations',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({ label: route.params.label }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/label/:label/conversations/:conversation_id'
    ),
    name: 'conversations_through_label',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      conversationId: route.params.conversation_id,
      label: route.params.label,
    }),
  },
  {
    path: frontendURL('accounts/:accountId/team/:teamId'),
    name: 'team_conversations',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({ teamId: route.params.teamId }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/team/:teamId/conversations/:conversationId'
    ),
    name: 'conversations_through_team',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      conversationId: route.params.conversationId,
      teamId: route.params.teamId,
    }),
  },
  {
    path: frontendURL('accounts/:accountId/custom_view/:id'),
    name: 'folder_conversations',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    beforeEnter: redirectFolderListIfUnavailable,
    component: ConversationView,
    props: route => ({ foldersId: route.params.id }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/custom_view/:id/conversations/:conversation_id'
    ),
    name: 'conversations_through_folders',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    beforeEnter: redirectFolderConversationIfUnavailable,
    props: route => ({
      conversationId: route.params.conversation_id,
      foldersId: route.params.id,
    }),
  },
  {
    path: frontendURL('accounts/:accountId/mentions/conversations'),
    name: 'conversation_mentions',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: () => ({ conversationType: 'mention' }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/mentions/conversations/:conversationId'
    ),
    name: 'conversation_through_mentions',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      conversationId: route.params.conversationId,
      conversationType: 'mention',
    }),
  },
  {
    path: frontendURL('accounts/:accountId/unattended/conversations'),
    name: 'conversation_unattended',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: () => ({ conversationType: 'unattended' }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/unattended/conversations/:conversationId'
    ),
    name: 'conversation_through_unattended',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      conversationId: route.params.conversationId,
      conversationType: 'unattended',
    }),
  },
  {
    path: frontendURL('accounts/:accountId/ia/conversations'),
    name: 'conversation_ai',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: () => ({ conversationType: 'ai' }),
  },
  {
    path: frontendURL('accounts/:accountId/ia/conversations/:conversationId'),
    name: 'conversation_through_ai',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      conversationId: route.params.conversationId,
      conversationType: 'ai',
    }),
  },
  {
    path: frontendURL('accounts/:accountId/emails/conversations'),
    name: 'conversation_emails',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: () => ({ conversationType: 'email' }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/emails/conversations/:conversationId'
    ),
    name: 'conversation_through_emails',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      conversationId: route.params.conversationId,
      conversationType: 'email',
    }),
  },
  {
    path: frontendURL('accounts/:accountId/emails/inbox/:inbox_id'),
    name: 'email_inbox_conversations',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      inboxId: route.params.inbox_id,
      conversationType: 'email',
    }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/emails/inbox/:inbox_id/conversations/:conversationId'
    ),
    name: 'conversation_through_email_inbox',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      inboxId: route.params.inbox_id,
      conversationId: route.params.conversationId,
      conversationType: 'email',
    }),
  },
  {
    path: frontendURL('accounts/:accountId/emails/folders/:folder(junk|trash)'),
    name: 'email_folder',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: EmailFolderView,
  },
  {
    path: frontendURL('accounts/:accountId/resolved/conversations'),
    name: 'conversation_resolved',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: () => ({ conversationType: 'resolved' }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/resolved/conversations/:conversationId'
    ),
    name: 'conversation_through_resolved',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      conversationId: route.params.conversationId,
      conversationType: 'resolved',
    }),
  },
  {
    path: frontendURL('accounts/:accountId/participating/conversations'),
    name: 'conversation_participating',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: () => ({ conversationType: 'participating' }),
  },
  {
    path: frontendURL(
      'accounts/:accountId/participating/conversations/:conversationId'
    ),
    name: 'conversation_through_participating',
    meta: {
      permissions: CONVERSATION_PERMISSIONS,
    },
    component: ConversationView,
    props: route => ({
      conversationId: route.params.conversationId,
      conversationType: 'participating',
    }),
  },
];

// Toda tela que renderiza ConversationView (lista ou conversa aberta) é tela de
// conversas: o layout não mostra o breadcrumb global nela, para não deslocar a tela
// ao alternar entre a lista e uma conversa.
export default {
  routes: conversationRoutes.map(route =>
    route.component === ConversationView
      ? { ...route, meta: { ...route.meta, conversationScreen: true } }
      : route
  ),
};
